import { createContext } from 'react';
import { Firebase } from '@platform/core';
import uuid4 from 'uuid4';

export class API {
  constructor() {
    this.host = process.env.WEB_SERVER_URL;
    this.c_host = process.env.COMPUTING_SERVER_URL;
  }

  async getIdToken() {
    const user = Firebase.getCurrentUser();
    const token = await user.getIdToken();
    return token;
  }

  async getResponseData(response) {
    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.indexOf('application/json') !== -1) {
      return await response.json();
    } else {
      return null;
    }
  }

  async handleError(res) {
    if (res.status >= 400 && res.status <= 500) {
      const data = await this.getResponseData(res);
      if (data?.detail) {
        throw new Error(data.detail);
      } else {
        if (res.status === 401) {
          throw new Error('Unauthorized');
        } else if (res.status === 403) {
          throw new Error('Forbidden');
        } else {
          throw new Error('Internal Server Error');
        }
      }
    }
  }

  async log(data) {
    const url = `${this.host}/api/v1/log`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
  }

  isEnabled() {
    return true;
  }

  _uuid() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return s4() + s4();
  }

  async getAuth() {
    const url = `${this.host}/account/auth`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  /** Users */
  async getUsers() {
    const url = `${this.host}/account/users`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getUser(user_id) {
    const url = `${this.host}/account/users/${user_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createUser(data) {
    const url = `${this.host}/account/user`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateUser(user_id, data) {
    const url = `${this.host}/account/users/${user_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteUser(user_id, data) {
    const url = `${this.host}/account/users/${user_id}/status`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  /** Sites */
  async getSites() {
    const url = `${this.host}/api/v1/sites`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getSite(site_id) {
    const url = `${this.host}/api/v1/sites/${site_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createSite(data) {
    const url = `${this.host}/api/v1/sites`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteSite(site_id, data) {
    const url = `${this.host}/api/v1/sites/${site_id}/status`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  /** Trials */
  async getTrials(site_id) {
    const url = site_id
      ? `${this.host}/api/v1/sites/${site_id}/trials`
      : `${this.host}/api/v1/trials`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getTrial(trial_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createTrial(site_id, data) {
    const url = `${this.host}/api/v1/sites/${site_id}/trials`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateTrial(trial_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteTrial(trial_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/status`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  /** Trial Users */
  async getTrialUsers(trial_id) {
    const url = `${this.host}/account/trials/${trial_id}/users`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createTrialUser(user_id, trial_id, data) {
    const url = `${this.host}/account/trials/${trial_id}/users/${user_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateTrialUser(user_id, trial_id, data) {
    const url = `${this.host}/account/trials/${trial_id}/users/${user_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteTrialUser(user_id, trial_id) {
    const url = `${this.host}/account/trials/${trial_id}/users/${user_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    return true;
  }

  /** Scenarios */
  async getScenarios(trial_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getScenario(trial_id, scenario_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createScenario(trial_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateScenario(trial_id, scenario_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteScenario(trial_id, scenario_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/status`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  /** Scenario Users */
  async getScenarioUsers(trial_id, scenario_id) {
    const url = `${this.host}/account/trials/${trial_id}/scenarios/${scenario_id}/users`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createScenarioUser(user_id, trial_id, scenario_id, data) {
    const url = `${this.host}/account/trials/${trial_id}/scenarios/${scenario_id}/users/${user_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateScenarioUser(user_id, trial_id, scenario_id, data) {
    const url = `${this.host}/account/trials/${trial_id}/scenarios/${scenario_id}/users/${user_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteScenarioUser(user_id, trial_id, scenario_id) {
    const url = `${this.host}/account/trials/${trial_id}/scenarios/${scenario_id}/users/${user_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    return true;
  }

  /** Subjects */
  async getSubjects(trial_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/subjects`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getSubject(trial_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/subjects/${subject_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getScenarioSubjects(trial_id, scenario_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getScenarioSubject(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async confirmSubjectInfo(trial_id, subject_id, info_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/subjects/${subject_id}/information/${info_id}/confirm`;
    const body = JSON.stringify({});
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async getSubjectSeries(trial_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/subjects/${subject_id}/series`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getScenarioSubjectSeries(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/series`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async updateSeries(trial_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/series`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteSeries(trial_id, subject_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/subjects/${subject_id}/series`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async createSeriesTag(trial_id, series_uid, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/series/${series_uid}/tags`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateTimepointStatus(
    trial_id,
    scenario_id,
    subject_id,
    timepoint_id,
    data
  ) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/timepoints/${timepoint_id}/status`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async downloadReports(trial_id, scenario_id) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/reports`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const filename = res.headers.get('Content-Disposition').split('"')[1];
    const blob = await res.blob();
    const _url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = _url;
    a.download = `${filename}.zip`;
    try {
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      a.remove();
    }
    return `${filename}.zip`;
  }

  async getSnapshots(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/snapshots`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getApprovedSnapshots(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/snapshots`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData.filter((s) => s.status === 'APPROVED');
  }

  async updateSnapshot(
    trial_id,
    scenario_id,
    subject_id,
    timepoint_id,
    snapshot_id,
    data
  ) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/timepoints/${timepoint_id}/snapshots/${snapshot_id}/status`;
    const query = `?status=${data.status}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url + query, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  sortLinks(links) {
    const customOrder = ['TARGET', 'NON-TARGET', 'NEW'];
    const orderMap = new Map(customOrder.map((item, index) => [item, index]));
    links.sort((a, b) => {
      const orderA =
        orderMap.get(a.type) !== undefined ? orderMap.get(a.type) : Infinity;
      const orderB =
        orderMap.get(b.type) !== undefined ? orderMap.get(b.type) : Infinity;
      return orderA - orderB;
    });
  }

  async getLinks(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/links`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    this.sortLinks(resData);
    return resData;
  }

  async createLink(trial_id, scenario_id, subject_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/links`;
    const id = uuid4();
    const body = JSON.stringify({ ...data, id });
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return id;
  }

  async updateLink(trial_id, scenario_id, subject_id, link_id, data) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/links/${link_id}`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteLink(trial_id, scenario_id, subject_id, link_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/links/${link_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    return true;
  }

  async resetLink(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/links/reset`;
    const body = JSON.stringify({});
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async updateLinkTimepointStatus(
    trial_id,
    scenario_id,
    subject_id,
    timepoint_id,
    data
  ) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/timepoints/${timepoint_id}/status`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async getAssessments(trial_id, scenario_id, subject_id) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/assessments`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async updateAssessment(
    trial_id,
    scenario_id,
    subject_id,
    timepoint_id,
    data
  ) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/subjects/${subject_id}/timepoints/${timepoint_id}/assessments`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async getWorkflows(trial_id, scenario_id, page, query) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/workflows?cursor=${page}&query=${query}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async getWorkflow(trial_id, scenario_id, workflow_id) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/workflows/${workflow_id}?cursor=${page}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData;
  }

  async createWorkflow(trial_id, scenario_id, data) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/workflows`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async deleteWorkflow(trial_id, scenario_id, workflow_id) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/workflows/${workflow_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    return true;
  }

  async getTasks(trial_id) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/uploads`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData.records.reverse();
  }

  async uploadFile(trial_id, file) {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.c_host}/api/v1/trials/${trial_id}/uploads/zip`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
      body: formData,
    });
    await this.handleError(res);
    return true;
  }

  async uploadCsv(trial_id, file) {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.c_host}/api/v1/trials/${trial_id}/uploads/csv`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
      body: formData,
    });
    await this.handleError(res);
    return true;
  }

  async uploadRTSTRUCT(trial_id, scenario_id, file) {
    const formData = new FormData();
    formData.append('rtstruct_file', file);
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/rtstructs`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
      body: formData,
    });
    await this.handleError(res);
    const resData = await res.json();
    return resData.snapshot_id;
  }

  async mergeRTSTRUCT(trial_id, scenario_id, data) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/scenario_id/${scenario_id}/rtstructs/merge`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async approveRTSTRUCT(
    trial_id,
    scenario_id,
    subject_id,
    timepoint_id,
    series_uid,
    snapshot_id
  ) {
    const series = await this.getScenarioSubjectSeries(
      trial_id,
      scenario_id,
      subject_id
    );
    const snapshots = await this.getApprovedSnapshots(
      trial_id,
      scenario_id,
      subject_id
    );
    for (const s of series) {
      if (s.modality === 'RTSTRUCT' && s.referenced_series_uid === series_uid) {
        const _snapshot = snapshots.find(
          (rt) => rt.series_uid === s.series_uid
        );
        if (_snapshot?._id) {
          await this.updateSnapshot(
            trial_id,
            scenario_id,
            subject_id,
            _snapshot.timepoint_id,
            _snapshot._id,
            { status: 'UNAPPROVED' }
          );
        }
      }
    }
    if (snapshot_id) {
      await this.updateSnapshot(
        trial_id,
        scenario_id,
        subject_id,
        timepoint_id,
        snapshot_id,
        { status: 'APPROVED' }
      );
    }
  }

  async uploadPostProcess(trial_id, upload_id) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/uploads/${upload_id}`;
    const body = JSON.stringify({});
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    await this.handleError(res);
    return true;
  }

  async downloadResult(trial_id, upload_id, filename) {
    const url = `${this.c_host}/api/v1/trials/${trial_id}/uploads/${upload_id}`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'x-token': `${token}`,
      },
    });
    await this.handleError(res);
    const blob = await res.blob();
    const _url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = _url;
    a.download = `upload_result_${filename}`
      .replace('.zip', '.json')
      .replace('.csv', '.json');
    try {
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      a.remove();
    }
  }

  async getDICOMSeries(trial_id, subject_id, naturalized = true) {
    const url = `${this.host}/api/v1/trials/${trial_id}/dicom-web/series?PatientID=${subject_id}&limit=5000&offset=0&fuzzymatching=false&includefield=all`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    const series = await res.json();
    if (!series) return [];
    if (!naturalized) return series;
    const naturalizedSeries = series.map((s) => ({
      StudyDescription: _.get(s, ['00081030', 'Value', 0], ''),
      SeriesDescription: _.get(s, ['0008103E', 'Value', 0], ''),
      StudyInstanceUID: _.get(s, ['0020000D', 'Value', 0], ''),
      SeriesInstanceUID: _.get(s, ['0020000E', 'Value', 0], ''),
      SpecificCharacterSet: _.get(s, ['00080005', 'Value', 0], ''),
      StudyDate: _.get(s, ['00080020', 'Value', 0], ''),
      SeriesDate: _.get(s, ['00080021', 'Value', 0], ''),
      StudyTime: _.get(s, ['00080030', 'Value', 0], '').split('.')[0],
      SeriesTime: _.get(s, ['00080031', 'Value', 0], '').split('.')[0],
      TimezoneOffsetFromUTC: _.get(s, ['00080201', 'Value', 0], ''),
      AccessionNumber: _.get(s, ['00080050', 'Value', 0], ''),
      Modality: _.get(s, ['00080060', 'Value', 0], ''),
      PatientName: _.get(s, ['00100010', 'Value', 0, 'Alphabetic'], ''),
      PatientID: _.get(s, ['00100020', 'Value', 0], ''),
      PatientBirthDate: _.get(s, ['00100030', 'Value', 0], ''),
      PatientSex: _.get(s, ['00100040', 'Value', 0], ''),
      StudyID: _.get(s, ['00200010', 'Value', 0], ''),
      SeriesNumber: _.get(s, ['00200011', 'Value', 0], ''),
    }));
    return naturalizedSeries;
  }

  async getScenarioDICOMSeries(trial_id, scenario_id, subject_id, naturalized = true) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/dicom-web/series?PatientID=${subject_id}&limit=5000&offset=0&fuzzymatching=false&includefield=all`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'x-token': `${token}`,
      },
    });
    const series = await res.json();
    if (!series) return [];
    if (!naturalized) return series;
    const naturalizedSeries = series.map((s) => ({
      StudyDescription: _.get(s, ['00081030', 'Value', 0], ''),
      SeriesDescription: _.get(s, ['0008103E', 'Value', 0], ''),
      StudyInstanceUID: _.get(s, ['0020000D', 'Value', 0], ''),
      SeriesInstanceUID: _.get(s, ['0020000E', 'Value', 0], ''),
      SpecificCharacterSet: _.get(s, ['00080005', 'Value', 0], ''),
      StudyDate: _.get(s, ['00080020', 'Value', 0], ''),
      SeriesDate: _.get(s, ['00080021', 'Value', 0], ''),
      StudyTime: _.get(s, ['00080030', 'Value', 0], '').split('.')[0],
      SeriesTime: _.get(s, ['00080031', 'Value', 0], '').split('.')[0],
      TimezoneOffsetFromUTC: _.get(s, ['00080201', 'Value', 0], ''),
      AccessionNumber: _.get(s, ['00080050', 'Value', 0], ''),
      Modality: _.get(s, ['00080060', 'Value', 0], ''),
      PatientName: _.get(s, ['00100010', 'Value', 0, 'Alphabetic'], ''),
      PatientID: _.get(s, ['00100020', 'Value', 0], ''),
      PatientBirthDate: _.get(s, ['00100030', 'Value', 0], ''),
      PatientSex: _.get(s, ['00100040', 'Value', 0], ''),
      StudyID: _.get(s, ['00200010', 'Value', 0], ''),
      SeriesNumber: _.get(s, ['00200011', 'Value', 0], ''),
    }));
    return naturalizedSeries;
  }

  async searchDICOMInstances(trial_id, scenario_id, study_uid, series_uid) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/dicom-web/studies/${study_uid}/series/${series_uid}/instances`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/dicom+json',
        'x-token': `${token}`,
      },
    });
    const instances = await res.json();
    if (!instances) return [];
    return instances;
  }

  async getDICOMInstance(trial_id, scenario_id, study_uid, series_uid, instance_uid) {
    const url = `${this.host}/api/v1/trials/${trial_id}/scenarios/${scenario_id}/dicom-web/studies/${study_uid}/series/${series_uid}/instances/${instance_uid}/metadata`;
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        accept: 'application/dicom+json',
        'x-token': `${token}`,
      },
    });
    const instances = await res.json();
    if (!instances) return {};
    return instances[0];
  }

  async segment(data) {
    const url = `${this.c_host}/api/v1/semi-automatic/contour`;
    const body = JSON.stringify(data);
    const token = await this.getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'x-token': `${token}`,
      },
      body: body,
    });
    this.handleError(res);
    const resData = await this.getResponseData(res);
    const { mask_contour } = resData;
    return mask_contour;
  }
}

export const APIContext = createContext();
